import React, { useState, useEffect } from "react";
import { useAuth } from "hooks/useAuth";
import { horusApi } from "services/api/horus";
import { Row, Col } from "react-bootstrap";
import { SelectBox } from "devextreme-react/select-box";
import { useGroupedItems } from "hooks/useGroupedItems";
import { getClientPalette, defaultPalette } from "config/clientPalettes";
import EmptyCard from "components/common/pyramid/EmptyCard";
import PyramidCard from "components/common/pyramid/PyramidCard";
import AmortizationByMaturity from "./AmortizationByMaturity";
import AmortizationBySector from "./AmortizationBySector";
import AmortizationByInterestType from "./AmortizationByInterestType";
import AmortizationByAccountingMethod from "./AmortizationByAccountingMethod";
import AmortizationByBounds from "./AmortizationByBounds";
import { HORUS_AMORTIZATION_TYPE } from "data/ENUM/add-options";

/**
 * ComputationCharts Component
 *
 * Main dashboard component for displaying various financial charts and breakdowns.
 * Fetches computation data, processes it, and distributes it to child components.
 */
const ComputationChartsHorus = () => {
  // State management for chart data, computations, and UI controls
  const [chartData, setChartData] = useState({
    amortizationByMaturityData: [],
    amortizationBySectorData: [],
    amortizationByInterestTypeData: [],
    amortizationByAccountingMethodData: [],
    amortizationByBounds: [],
    sumAmortizationTotal: null,
  });
  const [rawChartData, setRawChartData] = useState([]); // Store all raw data from API
  const [computations, setComputations] = useState([]);
  const [selectedComputation, setSelectedComputation] = useState(null);
  const [currentPalette, setCurrentPalette] = useState(defaultPalette);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedAmortizationType, setSelectedAmortizationType] = useState(
    HORUS_AMORTIZATION_TYPE[3].id
  );

  // Get authentication data and API instance
  const { accessToken, account, companyList, userList } = useAuth();
  const api = horusApi();

  // Grouping computations by user
  const groupedComputations = useGroupedItems(
    computations,
    userList,
    companyList
  );

  /**
   * Helper function to get chart name based on base name and amortization type
   * For other types that are not "All", appends the type as suffix (e.g., "Linear", "Actuarial", "Constant")
   */
  const getChartName = (baseName, amortizationType) => {
    if (amortizationType === "All") {
      return baseName;
    }
    return `${baseName}${amortizationType}`;
  };

  /**
   * Parse raw chart data based on selected amortization type
   * Filters and transforms data from rawChartData without making API calls
   */
  const parseChartDataByType = (rawData, amortizationType) => {
    if (!rawData || rawData.length === 0) {
      return {
        amortizationByMaturityData: [],
        amortizationBySectorData: [],
        amortizationByInterestTypeData: [],
        amortizationByAccountingMethodData: [],
        amortizationByBounds: [],
        sumAmortizationTotal: null,
      };
    }

    // Get chart names based on selected amortization type
    const maturityChartName = getChartName(
      "AmortizationByMaturity",
      amortizationType
    );
    const sectorChartName = getChartName(
      "AmortizationBySector",
      amortizationType
    );
    const interestTypeChartName = getChartName(
      "AmortizationByInterestType",
      amortizationType
    );
    const finalSumChartName =
      amortizationType === "All"
        ? "SumAmortizationTotal"
        : `SumAmortization${amortizationType}`;

    return {
      amortizationByMaturityData:
        rawData
          .find((chart) => chart.Name === maturityChartName)
          ?.Data.map((item) => ({
            interval: item.Interval,
            initialDiscount: item.InitialDiscount,
            amortization: item.Amortization,
            remainingDiscount: item.RemainingDiscount,
          })) || [],

      amortizationBySectorData:
        rawData
          .find((chart) => chart.Name === sectorChartName)
          ?.Data.map((item) => ({
            sector: item.Sector,
            initialDiscount: item.InitialDiscount,
            amortization: item.Amortization,
            remainingDiscount: item.RemainingDiscount,
          })) || [],
      amortizationByInterestTypeData:
        rawData
          .find((chart) => chart.Name === interestTypeChartName)
          ?.Data.map((item) => ({
            interestType: item.InterestType,
            initialDiscount: item.InitialDiscount,
            amortization: item.Amortization,
            remainingDiscount: item.RemainingDiscount,
          })) || [],
      amortizationByAccountingMethodData:
        rawData
          .find((chart) => chart.Name === "AmortizationByAccountingMethod")
          ?.Data.map((item) => ({
            accountingMethod: item.AccountingMethod,
            initialDiscount: item.InitialDiscount,
            amortization: item.Amortization,
            remainingDiscount: item.RemainingDiscount,
          })) || [],
      amortizationByBounds:
        rawData
          .find((chart) => chart.Name === "AmortizationByBound")
          ?.Data.map((item) => ({
            isBounded: item.IsBounded,
            amortization: item.Amortization,
          })) || [],
      sumAmortizationTotal:
        rawData.find((chart) => chart.Name === finalSumChartName)?.Data[0] ||
        null,
    };
  };

  /**
   * Effect to update the color palette when the user account changes
   * Each client may have a custom color palette configuration
   */
  useEffect(() => {
    if (account && companyList && companyList.length > 0) {
      const newPalette = getClientPalette(companyList[0].id || "default");
      setCurrentPalette(newPalette);
    }
  }, [account, companyList]);

  /**
   * Effect to fetch available computations when component mounts
   * Retrieves only the completed/ready computations and sorts them by creation date
   */
  useEffect(() => {
    const fetchComputations = async () => {
      try {
        if (!accessToken) {
          return;
        }

        const data = await api.getComputations({
          onlyReady: true,
          includeUserTransitiveGroups: true,
        });

        if (data) {
          const computations = Array.isArray(data) ? data : data.result || [];
          // Sort computations by creation date (newest first)
          const sortedComputations = [...computations].sort(
            (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
          );
          setComputations(sortedComputations);

          // Auto-select the most recent computation
          if (sortedComputations.length > 0) {
            setSelectedComputation(sortedComputations[0].id);
          }
        } else {
          console.error("Unexpected API response format:", data);
        }
      } catch (error) {
        console.error("Error while fetching computations:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchComputations();
  }, [accessToken]);

  /**
   * Effect to fetch raw chart data when the selected computation changes
   * Stores all data from API without filtering by amortization type
   */
  useEffect(() => {
    const fetchComputationCharts = async () => {
      if (!selectedComputation || !accessToken) return;

      try {
        const data = await api.getComputationCharts(selectedComputation);

        if (data) {
          const result =
            Array.isArray(data) && data.length > 0 ? data[0].Items : [];
          // Store all raw data - no filtering at this stage
          setRawChartData(result);
        }
      } catch (error) {
        console.error("Error while fetching chart data:", error);
        setRawChartData([]);
      }
    };

    fetchComputationCharts();
  }, [selectedComputation, accessToken]);

  /**
   * Effect to parse and filter chart data when amortization type changes
   * Uses already-fetched rawChartData, no API call needed
   */
  useEffect(() => {
    const parsedData = parseChartDataByType(
      rawChartData,
      selectedAmortizationType
    );
    setChartData(parsedData);
  }, [rawChartData, selectedAmortizationType]);

  /* Function used to format the title (adds Amortization type if needed for ChartCards titles)*/
  const getFormattedChartTitle = (baseTitle, amortizationType) => {
    if (amortizationType === "All") {
      return baseTitle;
    }
    return `${baseTitle} (${amortizationType})`;
  };

  /**
   * Charts are displayed in two sections : filtered and non-filtered
   * The filtered section displays charts dynamically based on the chosen amortization type
   * and the number of charts
   */
  return (
    <div>
      {/* Header Row - Amortization Total and Computation Selector */}
      <Row className="g-3 mb-3">
        <Col md={5}>
          <EmptyCard chartData={chartData.sumAmortizationTotal} />
        </Col>

        <Col md={7}>
          <PyramidCard className="h-100">
            <div className="d-flex flex-column h-100 py-2 justify-content-around">
              <div className="fw-medium fs-7 text-nowrap mt-n2">
                Computation
              </div>
              <SelectBox
                dataSource={groupedComputations}
                displayExpr="description"
                valueExpr="id"
                grouped={true}
                value={selectedComputation}
                onValueChanged={(e) => setSelectedComputation(e.value)}
                disabled={isLoading}
                searchEnabled={true}
                className="w-100"
              />
            </div>
          </PyramidCard>
        </Col>
      </Row>

      {/* Filtered Charts Section */}
      <div className="mb-4">
        {/* Section Header with Filter */}
        <div className="mb-3">
          <PyramidCard className="border-start border-primary border-3">
            <div className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between p-3 gap-2">
              <div className="p-3 d-flex align-items-center">
                <span
                  className="badge bg-primary me-2"
                  style={{ paddingTop: "0.5rem" }}
                >
                  Filtered
                </span>
                <span className="text-muted fs-7">
                  Charts filtered by amortization type
                </span>
              </div>
              <div className="d-flex align-items-center flex-shrink-0">
                <label className="text-nowrap me-2 fw-medium fs-7 mb-0">
                  Amortization type:
                </label>
                <SelectBox
                  dataSource={HORUS_AMORTIZATION_TYPE}
                  displayExpr="name"
                  valueExpr="id"
                  value={selectedAmortizationType}
                  onValueChanged={(e) => setSelectedAmortizationType(e.value)}
                  width={180}
                  className="dx-font"
                />
              </div>
            </div>
          </PyramidCard>
        </div>

        {/* Filtered Charts Grid - Only show charts that have data for selected type */}
        {chartData.amortizationByMaturityData.length > 0 && (
          <Row className="g-3">
            <Col xs={12}>
              <AmortizationByMaturity
                data={chartData.amortizationByMaturityData}
                palette={currentPalette}
                title={getFormattedChartTitle(
                  "Amortization by Maturity",
                  selectedAmortizationType
                )}
              />
            </Col>
          </Row>
        )}

        {(chartData.amortizationBySectorData.length > 0 ||
          chartData.amortizationByInterestTypeData.length > 0) && (
            <Row
              className="g-3"
              style={{
                marginTop:
                  chartData.amortizationByMaturityData.length > 0 ? "1rem" : "0",
              }}
            >
              {chartData.amortizationBySectorData.length > 0 && (
                <Col
                  md={
                    chartData.amortizationByInterestTypeData.length > 0 ? 6 : 12
                  }
                >
                  <AmortizationBySector
                    data={chartData.amortizationBySectorData}
                    palette={currentPalette}
                    title={getFormattedChartTitle(
                      "Amortization by Sector",
                      selectedAmortizationType
                    )}
                  />
                </Col>
              )}
              {chartData.amortizationByInterestTypeData.length > 0 && (
                <Col md={chartData.amortizationBySectorData.length > 0 ? 6 : 12}>
                  <AmortizationByInterestType
                    data={chartData.amortizationByInterestTypeData}
                    palette={currentPalette}
                    title={getFormattedChartTitle(
                      "Amortization by Interest Type",
                      selectedAmortizationType
                    )}
                  />
                </Col>
              )}
            </Row>
          )}
      </div>

      {/* Non-Filtered Charts Section */}
      <div className="mb-3">
        {/* Section Header */}
        <div className="mb-3">
          <PyramidCard className="border-start border-secondary border-3">
            <div className="p-3 d-flex align-items-center">
              <span
                className="badge bg-secondary me-2"
                style={{ paddingTop: "0.5rem" }}
              >
                Unfiltered
              </span>
              <span className="text-muted fs-7">
                Charts independent of amortization type
              </span>
            </div>
          </PyramidCard>
        </div>

        {/* Non-Filtered Charts Grid */}
        <Row className="g-3">
          <Col md={6}>
            <AmortizationByBounds
              data={chartData.amortizationByBounds}
              palette={currentPalette}
            />
          </Col>
          <Col md={6}>
            <AmortizationByAccountingMethod
              data={chartData.amortizationByAccountingMethodData}
              palette={currentPalette}
            />
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default ComputationChartsHorus;
